import React, { useCallback, useState } from 'react'

import { Stack, Typography, useTheme } from '@mui/material'
import { RadioReceiverIcon } from 'lucide-react'

import { Alert, Button, Modal } from '@repo/ui'
import { useEncodingDevice, useSnackbar } from 'contexts'
import { getErrorMessage } from 'utils'
import { api } from 'services'

/**
 * VERA-790 Phase 1 — "package handle": write a PLAIN NDEF URL label to a fresh tag stuck on the
 * physical box, so an operator can later scan the box to resolve the package. This is NOT a
 * SUN-verified product tag (no EV2 lock, no proof) — see EncodeTagsModal for that flow.
 *
 * The resolve host mirrors the SAME default the product-tag (SUN) encode session relies on: the
 * platform never passes a `host` to `encode()` (useEncodeSession), so the reader-helper's own
 * default (`api.dev.vera.inc`, apps/reader-helper/index.js) is what every existing encoded tag
 * already points at. Kept as a local constant here since there is no shared platform env/config
 * for it today (assumption flagged in the task write-up).
 */
const HANDLE_VERIFY_HOST = 'api.dev.vera.inc'

interface EncodePackageHandleModalProps {
  packageObj: { id: string; name: string } | null
  open: boolean
  onClose: () => void
  refetch: () => void
}

export const EncodePackageHandleModal = (props: EncodePackageHandleModalProps) => {
  const { packageObj, open, onClose, refetch } = props
  const theme = useTheme()
  const { open: notification } = useSnackbar()
  const { helperReachable, isConnected, readerName, encodeHandle } = useEncodingDevice()

  const [encoding, setEncoding] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const readerReady = helperReachable && isConnected

  const handleClose = useCallback(() => {
    if (encoding) return
    setError(null)
    onClose()
  }, [encoding, onClose])

  const handleEncode = useCallback(async () => {
    if (!packageObj) return
    setEncoding(true)
    setError(null)
    const url = `https://${HANDLE_VERIFY_HOST}/pkg/${packageObj.id}`
    try {
      const result = await encodeHandle({ url })
      if (!result.ok || !result.uid) {
        setError(result.error || 'Encode failed — present a tag and try again.')
        return
      }
      await api.post(`/packages/${packageObj.id}/handle`, { uid: result.uid })
      notification({ type: 'success', title: 'Package label encoded' })
      refetch()
      setError(null)
      onClose()
    } catch (err) {
      setError(getErrorMessage(err))
    } finally {
      setEncoding(false)
    }
  }, [packageObj, encodeHandle, notification, refetch, onClose])

  return (
    <Modal
      title="Add package tag"
      open={open}
      onClose={handleClose}
      actions={
        <>
          <Button variant="secondary" onClick={handleClose} disabled={encoding}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleEncode} disabled={!readerReady || encoding}>
            {encoding ? 'Encoding…' : 'Encode label'}
          </Button>
        </>
      }
    >
      <Stack spacing={5}>
        <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          Present a blank NFC tag on the reader to set the package tag for{' '}
          <strong>{packageObj?.name}</strong>. This tag identifies the box. It is not a verified
          product tag.
        </Typography>

        <Stack
          direction="row"
          spacing={3}
          alignItems="center"
          sx={{
            border: `1px solid ${theme.palette.primary[100]}`,
            borderRadius: 2,
            px: 4,
            py: 3,
          }}
        >
          <RadioReceiverIcon
            strokeWidth={1}
            color={readerReady ? theme.palette.brand[500] : theme.palette.text.disabled}
          />
          <Stack spacing={0.5} sx={{ flex: 1 }}>
            <Typography variant="body3">
              {readerReady
                ? readerName || 'Reader connected'
                : helperReachable
                  ? 'No reader detected'
                  : 'Reader helper not running'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {readerReady
                ? 'Ready — present the tag, then click Encode label.'
                : 'Start the Vera Reader Helper and plug in the reader to continue.'}
            </Typography>
          </Stack>
        </Stack>

        {error && <Alert severity="error" description={error} sx={{ padding: '12px 24px' }} />}
      </Stack>
    </Modal>
  )
}
